import { forwardRef, type ReactNode } from "react";
import type { LucideIcon, LucideProps } from "lucide-react";

/**
 * Pixel viewfinder brackets framing the `CodesmithIcon` anvil on an 18-cell
 * grid, so every cell lands on one pixel at the 18px nav size.
 */
const PROOF_PIXELS =
  "M0 0h4v1H1v3H0zM14 0h4v4h-1V1h-3zM0 14h1v3h3v1H0zM17 14h1v4h-4v-1h3z" +
  "M4 5h10v1H4zM14 6h1v6h-1zM3 6h1v4H3zM4 9h1v1H4zM5 10h1v1H5zM6 11h1v2H6zM7 12h7v1H7z" +
  "M8 7h1v3H8zM11 7h1v3h-1z";

/**
 * The same glyph at two cells per stroke on a 24-cell grid, matching the
 * 2/24 stroke of lucide icons and `CodesmithIcon` when rendered side by side.
 */
const PROOF_BOLD_BRACKETS =
  "M0 0h6v2H2v4H0zM18 0h6v6h-2V2h-4zM0 18h2v4h4v2H0zM22 18h2v6h-6v-2h4z";
const PROOF_BOLD_ANVIL =
  "M5 6h14v2H5zM19 8h2v8h-2zM3 8h2v4H3zM5 12h2v2H5zM7 14h2v2H7zM7 16h12v2H7z" +
  "M11 10h2v4h-2zM15 10h2v4h-2z";

function pixelIcon(
  name: string,
  grid: number,
  children: ReactNode,
): LucideIcon {
  const Icon = forwardRef<SVGSVGElement, LucideProps>(function PixelIcon(
    { size = 24, ...props },
    ref,
  ) {
    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox={`0 0 ${grid} ${grid}`}
        fill="currentColor"
        aria-hidden
        {...props}
      >
        {children}
      </svg>
    );
  });
  Icon.displayName = name;
  return Icon;
}

/** Every surface that identifies Proof renders this glyph, never a lucide icon of its own. */
export const ProofIcon = pixelIcon("ProofIcon", 18, <path d={PROOF_PIXELS} />);

/**
 * For rows where Proof sits among 2/24-stroke lucide icons, e.g. the sidebar
 * nav. The bold strokes are 3 device px at 18px on 2x screens but would smear
 * across 1x pixels, so 1x keeps the crisp 1px glyph. Both share a 72-cell box
 * (4 x 18, 3 x 24). A 24-cell is 1.5 device px at 2x and every anvil x edge
 * sits on an odd cell, so the anvil shifts half a device px left onto whole
 * pixels.
 */
export const ProofIconBold = pixelIcon(
  "ProofIconBold",
  72,
  <>
    <path
      d={PROOF_PIXELS}
      transform="scale(4)"
      className="[@media(min-resolution:2dppx)]:hidden"
    />
    <g className="hidden [@media(min-resolution:2dppx)]:inline">
      <path d={PROOF_BOLD_BRACKETS} transform="scale(3)" />
      <path d={PROOF_BOLD_ANVIL} transform="translate(-1 0) scale(3)" />
    </g>
  </>,
);
